import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { syncAccountBalance } from '../lib/accountBalance';
import { broadcastTransactionChange } from './useTransactions';
import { logAudit } from '../lib/audit';
import { log, logError, startTimer } from '../lib/logger';
import { trackMetric } from '../lib/metrics';

export interface CreateTransferPayload {
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
  notes?: string;
  status?: 'pago' | 'pendente';
}

export interface TransferTransaction {
  id: string;
  transfer_group_id: string;
  account_id: string;
  description: string;
  type: 'receita' | 'despesa';
  amount: number;
  date: string;
  status: string;
  notes: string;
}

export const useTransfers = () => {
  const { user } = useAuth();

  /**
   * Creates a transfer between two accounts atomically.
   * Generates 2 linked transactions (outgoing despesa + incoming receita)
   * with the same transfer_group_id.
   * If either insert fails, no partial state is left (both in same insert batch).
   */
  const createTransfer = useCallback(
    async (payload: CreateTransferPayload): Promise<{ success: boolean; message: string }> => {
      if (!user) return { success: false, message: 'Usuário não autenticado.' };
      const elapsed = startTimer();
      log({ flow: 'createTransfer', status: 'start', userId: user.id, entity: 'transfer', context: { amount: payload.amount, date: payload.date } });

      const { from_account_id, to_account_id, amount, date, notes = '', status = 'pago' } = payload;

      if (from_account_id === to_account_id) {
        return { success: false, message: 'A conta de origem e destino não podem ser iguais.' };
      }
      if (!amount || amount <= 0) {
        return { success: false, message: 'O valor da transferência deve ser maior que zero.' };
      }

      // Security: verify both accounts belong to this user
      const { data: fromAcct, error: fromErr } = await supabase
        .from('accounts')
        .select('id, name, user_id')
        .eq('id', from_account_id)
        .eq('user_id', user.id)
        .maybeSingle();

      const { data: toAcct, error: toErr } = await supabase
        .from('accounts')
        .select('id, name, user_id')
        .eq('id', to_account_id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (fromErr || toErr || !fromAcct || !toAcct) {
        return { success: false, message: 'Uma ou mais contas não encontradas ou sem permissão.' };
      }

      const transferGroupId = crypto.randomUUID();
      const outgoingStatus = status === 'pago' ? 'pago' : 'pendente';
      const incomingStatus = status === 'pago' ? 'recebido' : 'pendente';

      const baseRoute = `${fromAcct.name} → ${toAcct.name}`;

      const outgoing = {
        user_id: user.id,
        account_id: from_account_id,
        card_id: null,
        payment_method: 'account',
        description: `Transferência efetuada: ${baseRoute}`,
        category: 'Transferência',
        subcategory: null,
        type: 'despesa',
        amount,
        date,
        status: outgoingStatus,
        notes: notes ?? '',
        recurrence: 'none',
        installments: 1,
        installment_number: 1,
        installment_group_id: null,
        transfer_group_id: transferGroupId,
        transaction_subtype: 'transferencia',
        show_in_calendar: true,
      };

      const incoming = {
        user_id: user.id,
        account_id: to_account_id,
        card_id: null,
        payment_method: 'account',
        description: `Transferência recebida: ${baseRoute}`,
        category: 'Transferência',
        subcategory: null,
        type: 'receita',
        amount,
        date,
        status: incomingStatus,
        notes: notes ?? '',
        recurrence: 'none',
        installments: 1,
        installment_number: 1,
        installment_group_id: null,
        transfer_group_id: transferGroupId,
        transaction_subtype: 'transferencia',
        show_in_calendar: false,
      };

      // Single insert = atomic: if one fails, both fail
      const { error: insertErr } = await supabase
        .from('transactions')
        .insert([outgoing, incoming]);

      if (insertErr) {
        logError('createTransfer', `Erro ao criar transferência: ${insertErr.message}`, { userId: user.id, context: { code: insertErr.code }, durationMs: elapsed() });
        trackMetric('create_transfer', { flow: 'createTransfer', success: false, durationMs: elapsed() });
        return { success: false, message: `Erro ao criar transferência: ${insertErr.message}` };
      }

      // Sync both account balances in parallel
      await Promise.all([
        syncAccountBalance(from_account_id),
        syncAccountBalance(to_account_id),
      ]);

      // Audit log (silent failure)
      await logAudit({
        action: 'create',
        resource: 'payment',
        resource_id: transferGroupId,
        details: {
          type: 'transfer',
          from_account_id,
          from_account_name: fromAcct.name,
          to_account_id,
          to_account_name: toAcct.name,
          amount,
          date,
          transfer_group_id: transferGroupId,
          user_id: user.id,
          timestamp: new Date().toISOString(),
        },
      });

      log({ flow: 'createTransfer', status: 'success', userId: user.id, entity: 'transfer', recordId: transferGroupId, message: 'Transferência criada', context: { amount, date }, durationMs: elapsed() });
      trackMetric('create_transfer', { flow: 'createTransfer', success: true, durationMs: elapsed() });
      broadcastTransactionChange();
      return { success: true, message: 'Transferência realizada com sucesso!' };
    },
    [user]
  );

  /**
   * Deletes BOTH legs of a transfer by transfer_group_id.
   * Never allows partial deletion — if any row is missing, it aborts.
   */
  const deleteTransfer = useCallback(
    async (transferGroupId: string): Promise<{ success: boolean; message: string }> => {
      if (!user) return { success: false, message: 'Usuário não autenticado.' };
      const elapsed = startTimer();
      log({ flow: 'deleteTransfer', status: 'start', userId: user.id, entity: 'transfer', recordId: transferGroupId });

      // Collect legs before deletion to know which accounts to sync
      const { data: txs, error: fetchErr } = await supabase
        .from('transactions')
        .select('id, account_id')
        .eq('transfer_group_id', transferGroupId)
        .eq('user_id', user.id);

      if (fetchErr || !txs || txs.length === 0) {
        logError('deleteTransfer', 'Transferência não encontrada', { userId: user.id, recordId: transferGroupId });
        return { success: false, message: 'Transferência não encontrada.' };
      }

      // Soft delete: preserves audit trail and stays consistent with the rest of the system
      // Both legs receive the same deleted_at timestamp atomically via a single UPDATE
      const { error: deleteErr } = await supabase
        .from('transactions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('transfer_group_id', transferGroupId)
        .eq('user_id', user.id)
        .is('deleted_at', null);

      if (deleteErr) {
        logError('deleteTransfer', `Erro ao excluir: ${deleteErr.message}`, { userId: user.id, recordId: transferGroupId, context: { code: deleteErr.code }, durationMs: elapsed() });
        trackMetric('delete_transfer', { flow: 'deleteTransfer', success: false, durationMs: elapsed() });
        return { success: false, message: `Erro ao excluir transferência: ${deleteErr.message}` };
      }

      // Sync all affected accounts
      const accountIds = [
        ...new Set(
          (txs as { account_id: string | null }[])
            .map(t => t.account_id)
            .filter(Boolean) as string[]
        ),
      ];
      await Promise.all(accountIds.map(id => syncAccountBalance(id)));

      await logAudit({
        action: 'delete',
        resource: 'payment',
        resource_id: transferGroupId,
        details: {
          type: 'transfer_delete',
          transfer_group_id: transferGroupId,
          user_id: user.id,
          timestamp: new Date().toISOString(),
        },
      });

      log({ flow: 'deleteTransfer', status: 'success', userId: user.id, entity: 'transfer', recordId: transferGroupId, message: 'Transferência excluída (soft delete)', durationMs: elapsed() });
      trackMetric('delete_transfer', { flow: 'deleteTransfer', success: true, durationMs: elapsed() });
      broadcastTransactionChange();
      return { success: true, message: 'Transferência excluída com sucesso.' };
    },
    [user]
  );

  return { createTransfer, deleteTransfer };
};
